import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Mail } from 'lucide-react';
import { toast } from 'sonner';
import SenderSelect from './SenderSelect';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Compose a free-form email from a deal page (10/13, Josh: "for compose from a deal page, allow me to select the
 * sender from a drop down"). Recipients come from the deal's contacts (merchant / broker / attorney, same list the
 * document emails use); the From dropdown comes from GET /gmail/sender-options for the chosen recipient — a merchant
 * may be written to from a legal@ mailbox, anyone else only from an admin mailbox. Sends through POST /gmail/send
 * (kind "manual"), which is logged in email_logs against the deal.
 */
const ComposeDealEmailDialog = ({ dealId, dealNumber, open, onOpenChange, getAuthHeaders }) => {
  const [recipients, setRecipients] = useState([]);
  const [toRole, setToRole] = useState('merchant');
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sender, setSender] = useState(null);
  const [senderId, setSenderId] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open || !dealId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/deals/${dealId}/documents/email-context`, { headers: getAuthHeaders() });
        if (cancelled) return;
        const list = res.data?.recipients || [];
        setRecipients(list);
        const first = list.find((r) => r.type === 'merchant') || list[0];
        setToRole(first ? (first.type === 'merchant' ? 'merchant' : 'broker') : 'merchant');
        setTo(first?.email || '');
      } catch {
        if (!cancelled) setRecipients([]);
      }
    })();
    setSubject(`Deal #${dealNumber ?? ''}`.trim());
    setBody('');
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, dealId, dealNumber]);

  // The sender list depends on WHO gets the email (merchant → legal allowed; anyone else → admin mailboxes only).
  useEffect(() => {
    if (!open || !dealId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/gmail/sender-options`, { headers: getAuthHeaders(), params: { kind: 'manual', deal_id: dealId, to: toRole } });
        if (cancelled) return;
        setSender(res.data);
        setSenderId((cur) => (cur && (res.data?.options || []).some((o) => o.id === cur) ? cur : (res.data?.default_id || '')));
      } catch {
        if (!cancelled) setSender(null);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, dealId, toRole]);

  const pickRecipient = (r) => {
    setTo(r.email || '');
    setToRole(r.type === 'merchant' ? 'merchant' : 'broker');
  };

  const send = async () => {
    if (!to || !to.includes('@')) { toast.error('Enter a recipient email'); return; }
    if (!subject.trim()) { toast.error('Subject required'); return; }
    setSending(true);
    try {
      const res = await axios.post(`${API}/gmail/send`, {
        recipient_email: to.trim(), subject: subject.trim(), body, html: false,
        event_type: 'manual', deal_id: dealId, sender_id: senderId || null, to_role: toRole,
      }, { headers: getAuthHeaders() });
      if (res.data?.success) {
        toast.success(`Email sent to ${to}`);
        onOpenChange(false);
      } else {
        toast.error(res.data?.error || 'Failed to send email');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Mail className="h-5 w-5 text-blue-600" />Compose Email — Deal #{dealNumber ?? ''}</DialogTitle>
          <DialogDescription>Pick who it goes to, then which mailbox it comes from.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {recipients.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {recipients.map((r) => (
                <button key={`${r.type}-${r.email}`} type="button" onClick={() => pickRecipient(r)}
                  className={`text-xs rounded border px-2 py-1 ${to === r.email ? 'border-blue-500 text-blue-700 bg-blue-50' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                  data-testid={`compose-recipient-${r.type}`}>
                  {r.label} &lt;{r.email}&gt;
                </button>
              ))}
            </div>
          )}
          <div>
            <Label>To *</Label>
            <Input type="email" value={to} onChange={(e) => setTo(e.target.value)} placeholder="recipient@example.com" data-testid="compose-to" />
            <div className="flex items-center gap-3 mt-1 text-xs text-gray-600">
              <span>This recipient is the</span>
              {['merchant', 'broker'].map((r) => (
                <label key={r} className="flex items-center gap-1 cursor-pointer">
                  <input type="radio" name="compose-to-role" checked={toRole === r} onChange={() => setToRole(r)} />
                  {r === 'merchant' ? 'merchant' : 'broker / other'}
                </label>
              ))}
            </div>
          </div>
          <SenderSelect sender={sender} value={senderId} onChange={setSenderId} />
          <div>
            <Label>Subject *</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="compose-subject" />
          </div>
          <div>
            <Label>Message</Label>
            <textarea className="w-full px-3 py-2 border rounded-md text-sm min-h-[160px]" value={body} onChange={(e) => setBody(e.target.value)} data-testid="compose-body" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending}>Cancel</Button>
          <Button onClick={send} disabled={sending || !to} data-testid="compose-send">{sending ? 'Sending…' : 'Send Email'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ComposeDealEmailDialog;
